/**
 * CRRIBBON: HEX Color Utilities
 * 
 * Provides HEX parsing, normalization, and flashy gradient mapping
 * for ribbon styling in Deal Cards and Theme Labs.
 */

// ================================================================
// TYPES
// ================================================================

export interface HslColor {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

export interface RibbonGradientColors {
  /** gradientFrom - darker shade for gradient start */
  gradientFrom: string;
  /** gradientTo - lighter shade for gradient end */
  gradientTo: string;
  /** borderColor - lighter border with transparency */
  borderColor: string;
}

export interface RibbonStylingOverrides extends RibbonGradientColors {
  /** Canonical input: normalized 6-digit hex */
  ribbonBaseHex: string;
  /** Optional border hex override (null = AUTO derived from base) */
  ribbonBorderHex: string | null;
}

// ================================================================
// VALIDATION & NORMALIZATION
// ================================================================

/**
 * Validate and normalize a HEX color string.
 * Accepts #RGB or #RRGGBB (case-insensitive).
 * Returns null if invalid.
 */
export function normalizeHex(input: string): string | null {
  if (!input) return null;
  
  // Remove leading # if present
  let hex = input.trim();
  if (hex.startsWith('#')) {
    hex = hex.slice(1);
  }
  
  // Validate format: 3 or 6 hex digits
  const shortHexPattern = /^[0-9A-Fa-f]{3}$/;
  const longHexPattern = /^[0-9A-Fa-f]{6}$/;
  
  if (shortHexPattern.test(hex)) {
    // Expand #RGB to #RRGGBB
    const r = hex[0];
    const g = hex[1];
    const b = hex[2];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  
  if (longHexPattern.test(hex)) {
    return `#${hex.toUpperCase()}`;
  }
  
  return null;
}

/**
 * Check if a string is a valid HEX color (without normalizing).
 */
export function isValidHex(input: string): boolean {
  return normalizeHex(input) !== null;
}

// ================================================================
// HEX <-> HSL CONVERSION
// ================================================================

/**
 * Convert HEX color to HSL.
 */
export function hexToHsl(hex: string): HslColor | null {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  
  // Parse RGB values
  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  
  let h = 0;
  let s = 0;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL to HEX.
 */
export function hslToHex(hsl: HslColor): string {
  const { h, s, l } = hsl;
  const sNorm = s / 100;
  const lNorm = l / 100;
  
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (h < 60) {
    r = c; g = x; b = 0;
  } else if (h < 120) {
    r = x; g = c; b = 0;
  } else if (h < 180) {
    r = 0; g = c; b = x;
  } else if (h < 240) {
    r = 0; g = x; b = c;
  } else if (h < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }
  
  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Format HSL values as CSS HSL string (without hsl() wrapper).
 * Output: "H S% L%" for use in Tailwind/CSS vars
 */
export function formatHslString(hsl: HslColor): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

/**
 * Format HSL values with alpha as CSS HSLA string.
 * Output: "H S% L% / alpha"
 */
export function formatHslaString(hsl: HslColor, alpha: number): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}% / ${alpha}`;
}

// ================================================================
// FLASHY GRADIENT MAPPING
// ================================================================

/**
 * Clamp lightness to valid range [8, 92].
 */
function clampL(l: number): number {
  return Math.min(92, Math.max(8, l));
}

/**
 * Generate flashy gradient colors from a base HEX color.
 * 
 * From Base HEX:
 * - gradientFrom = "{H} {S}% {clampL(L - 16)}%"  (darker)
 * - gradientTo = "{H} {S}% {clampL(L + 10)}%"    (lighter)
 * 
 * Border color:
 * - If borderHex is null (AUTO): "{H_base} {S_base}% {clampL(L_base + 28)}% / 0.45"
 * - If borderHex provided: "{H_border} {S_border}% {clampL(L_border + 28)}% / 0.45"
 */
export function buildFlashyGradient(
  baseHex: string,
  borderHex?: string | null
): RibbonGradientColors | null {
  const baseHsl = hexToHsl(baseHex);
  if (!baseHsl) return null;
  
  // Gradient from (darker)
  const gradientFromHsl: HslColor = {
    h: baseHsl.h,
    s: baseHsl.s,
    l: clampL(baseHsl.l - 16),
  };
  
  // Gradient to (lighter)
  const gradientToHsl: HslColor = {
    h: baseHsl.h,
    s: baseHsl.s,
    l: clampL(baseHsl.l + 10),
  };
  
  // Border color
  let borderHsl: HslColor;
  if (borderHex) {
    const parsedBorderHsl = hexToHsl(borderHex);
    borderHsl = parsedBorderHsl ?? baseHsl;
  } else {
    borderHsl = baseHsl;
  }
  
  const borderLightHsl: HslColor = {
    h: borderHsl.h,
    s: borderHsl.s,
    l: clampL(borderHsl.l + 28),
  };
  
  return {
    gradientFrom: formatHslString(gradientFromHsl),
    gradientTo: formatHslString(gradientToHsl),
    borderColor: formatHslaString(borderLightHsl, 0.45),
  };
}

/**
 * Build full ribbon styling overrides from HEX inputs.
 */
export function buildRibbonStylingOverrides(
  baseHex: string,
  borderHex?: string | null
): RibbonStylingOverrides | null {
  const normalizedBase = normalizeHex(baseHex);
  if (!normalizedBase) return null;
  
  const gradients = buildFlashyGradient(normalizedBase, borderHex);
  if (!gradients) return null;
  
  const normalizedBorder = borderHex ? normalizeHex(borderHex) : null;
  
  return {
    ribbonBaseHex: normalizedBase,
    ribbonBorderHex: normalizedBorder,
    ...gradients,
  };
}

/**
 * Compute the AUTO border color preview from base HEX.
 * Returns the computed border HEX for display purposes.
 */
export function computeAutoBorderHex(baseHex: string): string | null {
  const baseHsl = hexToHsl(baseHex);
  if (!baseHsl) return null;
  
  const borderHsl: HslColor = {
    h: baseHsl.h,
    s: Math.max(10, baseHsl.s - 20), // Slightly desaturate for border
    l: clampL(baseHsl.l + 28),
  };
  
  return hslToHex(borderHsl);
}

/**
 * Create ribbon styling keys to remove for Undo operation.
 * Returns array of keys that should be deleted from overrides.
 */
export function getRibbonStylingKeys(): string[] {
  return [
    'ribbonBaseHex',
    'ribbonBorderHex',
    'gradientFrom',
    'gradientTo',
    'borderColor',
  ];
}
